// Add-on category catalog — Stream 3 PR-1.
//
// Single source of truth for:
//   - the ADD_ONS wizard step's category picker
//   - the listing detail page's "Available add-ons" section
//   - the search/browse page's nice-to-have filter chips
//   - the credentials-gating logic at the wizard's REVIEW step
//
// Stored at publicData.addOnCategories on the listing (custom-quote
// listings only). Each row references a key in this catalog. The
// requiresLicense / requiresCOI flags are derived from the catalog,
// not host-authored — the wizard normaliser stamps them at save
// time so the listing-side row matches the source of truth.

// Tier keys. The wizard renders one section per tier; the listing
// detail page groups add-ons by tier.
export const ADDON_TIERS = ['decor', 'fnb', 'production'];

export const ADDON_TIER_LABELS = {
  decor: 'Decor & styling',
  fnb: 'Food & beverage',
  production: 'Production',
};

export const ADDON_TIER_HELPERS = {
  decor: 'No license required.',
  fnb: 'Food permit + COI required (except serving staff, which is COI only).',
  production: 'COI required.',
};

// Add-on pricing units now come from the SHARED STARTING_PRICE_UNITS
// vocabulary (util/editListingHelpers.js) — kits and add-ons speak one
// language (per booking / per day / per person/guest / per item / per
// hour), no separate add-on list. Legacy 'per_guest' rows are aliased to
// 'per_person' by resolveStartingPriceUnit.

// Catalog. Order within each tier follows the handoff doc.
export const ADDON_CATEGORIES = [
  // ── Tier 1: Decor & styling ── (no license required)
  {
    key: 'florals_fresh',
    label: 'Fresh florals',
    tier: 'decor',
    requiresLicense: false,
    requiresCOI: false,
  },
  {
    key: 'florals_dried',
    label: 'Dried florals',
    tier: 'decor',
    requiresLicense: false,
    requiresCOI: false,
  },
  {
    key: 'balloons',
    label: 'Balloons',
    tier: 'decor',
    requiresLicense: false,
    requiresCOI: false,
  },
  {
    key: 'signage_calligraphy',
    label: 'Signage & calligraphy',
    tier: 'decor',
    requiresLicense: false,
    requiresCOI: false,
  },
  {
    key: 'additional_rentals',
    label: 'Additional rentals',
    tier: 'decor',
    requiresLicense: false,
    requiresCOI: false,
  },
  // 'day_of_styling' and 'day_of_breakdown' removed 2026-07-02 (owner):
  // redundant with the Setup fulfillment option, which covers both.
  // Stale listing rows with these keys are hidden by every consumer
  // (findAddonCategory → null) and dropped on next save by
  // normaliseAddOnCategories.

  // ── Tier 2: Food & beverage ── (food permit + COI required;
  // serving_staff is COI-only by design)
  {
    key: 'catering',
    label: 'Catering',
    tier: 'fnb',
    requiresLicense: true,
    requiresCOI: true,
  },
  {
    key: 'serving_staff',
    label: 'Serving staff',
    tier: 'fnb',
    requiresLicense: false,
    requiresCOI: true,
  },
  {
    key: 'bartending',
    label: 'Bartending',
    tier: 'fnb',
    requiresLicense: true,
    requiresCOI: true,
  },
  {
    key: 'dessert_cake',
    label: 'Dessert / cake',
    tier: 'fnb',
    requiresLicense: true,
    requiresCOI: true,
  },
  {
    key: 'coffee_beverages',
    label: 'Coffee & beverages',
    tier: 'fnb',
    requiresLicense: true,
    requiresCOI: true,
  },

  // ── Tier 2: Production ── (COI required)
  {
    key: 'photography',
    label: 'Photography',
    tier: 'production',
    requiresLicense: false,
    requiresCOI: true,
  },
  {
    key: 'videography',
    label: 'Videography',
    tier: 'production',
    requiresLicense: false,
    requiresCOI: true,
  },
  {
    key: 'dj_music',
    label: 'DJ / music',
    tier: 'production',
    requiresLicense: false,
    requiresCOI: true,
  },
  {
    key: 'event_coordination',
    label: 'Event coordination',
    tier: 'production',
    requiresLicense: false,
    requiresCOI: true,
  },
];

export const ADDON_CATEGORY_KEYS = ADDON_CATEGORIES.map(c => c.key);

/**
 * Look up a category by key. Returns null for an unknown key so
 * downstream code can render "unknown add-on" gracefully rather
 * than crash on a stale draft.
 */
export const findAddonCategory = key =>
  ADDON_CATEGORIES.find(c => c.key === key) || null;

/**
 * Return the categories in a given tier. Used by the wizard form
 * to render tier sections and by the listing detail page to group
 * the host's selected add-ons.
 */
export const categoriesForTier = tier =>
  ADDON_CATEGORIES.filter(c => c.tier === tier);

// Per-row default values. Used by both the form's "add a new
// add-on" path AND by normaliseAddOnCategories when a row from a
// stale draft is missing fields. Q1 lock: minLeadTimeDays required
// with default 7, floor 1.
export const ADDON_ROW_DEFAULTS = {
  startingAt: 0,
  pricingUnit: 'flat',
  description: '',
  minLeadTimeDays: 7,
  cancellationPolicy: 'moderate',
};

export const MIN_LEAD_TIME_DAYS_FLOOR = 1;

// Cap. Keeping the array bounded matches the per-listing item caps
// elsewhere in the codebase (the quote sheet has a 30-row cap;
// see normaliseQuoteSheet in editListingHelpers.js).
export const MAX_ADDONS_PER_LISTING = 20;
